'use client';

import { useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface ItineraryCardProps {
  itinerary: {
    id: string;
    title: string;
    destination: string;
    duration: string;
    price: number;
    originalPrice?: number;
    rating: number;
    reviewCount: number;
    image: string;
    alt: string;
    highlights: string[];
    availability: 'high' | 'medium' | 'low';
    pnrGuaranteed: boolean;
    tags: string[];
  };
  isSaved?: boolean;
  onSave?: (id: string) => void;
  onShare?: (itinerary: any) => void;
  compact?: boolean;
  theme?: string;
}

const ItineraryCard = ({ itinerary, isSaved = false, onSave, onShare, compact = false, theme = 'default' }: ItineraryCardProps) => {
  const [isHovered, setIsHovered] = useState(false);

  const getAvailabilityColor = (availability: string) => {
    switch (availability) {
      case 'high': return 'text-success';
      case 'medium': return 'text-warning';
      case 'low': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getAvailabilityText = (availability: string) => {
    switch (availability) {
      case 'high': return 'High Availability';
      case 'medium': return 'Limited Spots';
      case 'low': return 'Almost Full';
      default: return 'Check Availability';
    }
  };

  const cardBg = theme === 'dark' ? 'bg-gray-800 border border-gray-700' : 'bg-card';
  const titleColor = theme === 'dark' ? 'text-white' : 'text-foreground';
  const textColor = theme === 'dark' ? 'text-gray-300' : 'text-foreground';
  const mutedColor = theme === 'dark' ? 'text-gray-400' : 'text-muted-foreground';
  const tagBg = theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-surface text-muted-foreground';
  const borderColor = theme === 'dark' ? 'border-gray-600' : 'border-border';
  const imageHeight = compact ? 'h-40' : 'h-64';

  return (
    <div
      className={`${cardBg} rounded-lg shadow-brand hover:shadow-brand-lg transition-all duration-300 overflow-hidden group`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={`relative ${imageHeight} overflow-hidden`}>
        <AppImage
          src={itinerary.image}
          alt={itinerary.alt}
          className={`w-full h-full object-cover transition-transform duration-500 ${isHovered ? 'scale-110' : 'scale-100'}`}
        />

        {itinerary.pnrGuaranteed && (
          <div className="absolute top-4 left-4 bg-primary text-primary-foreground px-3 py-1.5 rounded-full text-xs font-heading font-semibold flex items-center space-x-1.5">
            <Icon name="ShieldCheckIcon" size={14} variant="solid" />
            <span>PNR Guaranteed</span>
          </div>
        )}

        {itinerary.originalPrice && (
          <div className="absolute top-4 right-4 bg-coral text-white px-3 py-1.5 rounded-full text-xs font-heading font-semibold">
            Save ${itinerary.originalPrice - itinerary.price}
          </div>
        )}

        <div className="absolute bottom-4 right-4 flex gap-2">
          {onSave && (
            <button
              onClick={(e) => { e.preventDefault(); onSave(itinerary.id); }}
              className={`w-8 h-8 rounded-full flex items-center justify-center shadow-md transition-all duration-200 ${
                isSaved ? 'bg-accent text-white' : 'bg-white/90 text-foreground hover:bg-accent hover:text-white'
              }`}
              title={isSaved ? 'Remove from saved' : 'Save itinerary'}
            >
              <Icon name="BookmarkIcon" size={14} variant={isSaved ? 'solid' : 'outline'} />
            </button>
          )}
          {onShare && (
            <button
              onClick={(e) => { e.preventDefault(); onShare(itinerary); }}
              className="w-8 h-8 rounded-full bg-white/90 text-foreground hover:bg-primary hover:text-white flex items-center justify-center shadow-md transition-all duration-200"
              title="Share itinerary"
            >
              <Icon name="ShareIcon" size={14} />
            </button>
          )}
        </div>

        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
          <div className="flex items-center space-x-2">
            <Icon name="MapPinIcon" size={16} className="text-white" variant="solid" />
            <span className="text-white text-sm font-medium">{itinerary.destination}</span>
          </div>
        </div>
      </div>

      <div className={compact ? 'p-3' : 'p-5'}>
        <div className="flex items-start justify-between mb-3">
          <h3 className={`font-heading font-semibold ${compact ? 'text-sm' : 'text-lg'} ${titleColor} line-clamp-2 flex-1`}>
            {itinerary.title}
          </h3>
        </div>

        <div className="flex items-center space-x-4 mb-4">
          <div className="flex items-center space-x-1">
            <Icon name="StarIcon" size={16} className="text-accent" variant="solid" />
            <span className={`text-sm font-semibold ${titleColor}`}>{itinerary.rating}</span>
            <span className={`text-sm ${mutedColor}`}>({itinerary.reviewCount})</span>
          </div>
          <div className={`flex items-center space-x-1.5 ${mutedColor}`}>
            <Icon name="ClockIcon" size={16} />
            <span className="text-sm">{itinerary.duration}</span>
          </div>
        </div>

        {!compact && (
          <div className="space-y-2 mb-4">
            {itinerary.highlights.slice(0, 3).map((highlight, index) => (
              <div key={index} className="flex items-start space-x-2">
                <Icon name="CheckCircleIcon" size={16} className="text-success mt-0.5 flex-shrink-0" variant="solid" />
                <span className={`text-sm ${textColor}`}>{highlight}</span>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-4">
          {itinerary.tags.slice(0, compact ? 2 : 3).map((tag, index) => (
            <span key={index} className={`px-2.5 py-1 ${tagBg} text-xs rounded-full`}>{tag}</span>
          ))}
        </div>

        <div className={`flex items-center justify-between pt-4 border-t ${borderColor}`}>
          <div>
            <div className="flex items-baseline space-x-2">
              <span className={`${compact ? 'text-lg' : 'text-2xl'} font-heading font-bold text-primary`}>
                ${itinerary.price.toLocaleString()}
              </span>
              {itinerary.originalPrice && (
                <span className={`text-sm ${mutedColor} line-through`}>${itinerary.originalPrice.toLocaleString()}</span>
              )}
            </div>
            <p className={`text-xs font-medium mt-1 ${getAvailabilityColor(itinerary.availability)}`}>
              {getAvailabilityText(itinerary.availability)}
            </p>
          </div>

          <Link
            href={`/booking-management?itinerary=${itinerary.id}`}
            className="px-5 py-2.5 bg-coral text-white text-sm font-heading font-semibold rounded-lg hover:bg-[#FF5722] transition-all duration-300 flex items-center space-x-2"
          >
            <span>{compact ? 'Book' : 'Book Now'}</span>
            <Icon name="ArrowRightIcon" size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ItineraryCard;